import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/Button';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { Input } from '@/components/ui/Input';
import type { MaterialWithStock, UpdateMaterialInput } from '@/services/admin';

interface MaterialManageCardProps {
  readonly material: MaterialWithStock;
  readonly onUpdate: (materialId: string, updates: UpdateMaterialInput) => void;
  readonly onToggleStock: (materialId: string, updates: { colorStock?: Record<string, boolean>; isActive?: boolean }) => void;
  readonly onDelete: (materialId: string) => void;
  readonly isUpdating: boolean;
}

export const MaterialManageCard = ({
  material,
  onUpdate,
  onToggleStock,
  onDelete,
  isUpdating,
}: MaterialManageCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newColor, setNewColor] = useState('');
  const [newColorCost, setNewColorCost] = useState('');
  const [newColorWeight, setNewColorWeight] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editForm, setEditForm] = useState({
    name: material.name,
    description: material.description,
    densityGPerCm3: String(material.densityGPerCm3 ?? '1.24'),
    estimatedDaysBase: String(material.estimatedDaysBase),
  });

  const totalInvestment = useMemo(() => {
    const spoolInfo = material.colorSpoolInfo ?? {};
    return Object.values(spoolInfo).reduce((sum, info) => sum + info.spoolCost, 0);
  }, [material.colorSpoolInfo]);

  const handleSaveEdit = () => {
    const changed: Record<string, unknown> = {};

    if (editForm.name !== material.name) changed.name = editForm.name;
    if (editForm.description !== material.description) changed.description = editForm.description;

    const density = Number(editForm.densityGPerCm3);
    if (!isNaN(density) && density !== material.densityGPerCm3) changed.densityGPerCm3 = density;

    const days = Number(editForm.estimatedDaysBase);
    if (!isNaN(days) && days !== material.estimatedDaysBase) changed.estimatedDaysBase = days;

    if (Object.keys(changed).length === 0) {
      setIsEditing(false);
      return;
    }

    onUpdate(material.materialId, changed as UpdateMaterialInput);
    setIsEditing(false);
  };

  const getDefaultSpoolCost = (): number => {
    const values = Object.values(material.colorSpoolInfo ?? {});
    return values.length > 0 ? values[0].spoolCost : 20;
  };

  const getDefaultSpoolWeight = (): number => {
    const values = Object.values(material.colorSpoolInfo ?? {});
    return values.length > 0 ? values[0].spoolWeightGrams : 1000;
  };

  const handleAddColor = () => {
    const color = newColor.trim();
    if (!color || material.colorOptions.includes(color)) {
      setNewColor('');
      return;
    }

    const cost = Number(newColorCost) || getDefaultSpoolCost();
    const weight = Number(newColorWeight) || getDefaultSpoolWeight();

    const updatedColors = [...material.colorOptions, color];
    const updatedStock: Record<string, boolean> = { ...material.colorStock, [color]: true };
    const updatedSpoolInfo = {
      ...material.colorSpoolInfo,
      [color]: { spoolCost: cost, spoolWeightGrams: weight },
    };

    onUpdate(material.materialId, {
      colorOptions: updatedColors,
      colorStock: updatedStock,
      colorSpoolInfo: updatedSpoolInfo,
    });
    setNewColor('');
    setNewColorCost('');
    setNewColorWeight('');
  };

  const handleRemoveColor = (colorToRemove: string) => {
    const updatedColors = material.colorOptions.filter((c) => c !== colorToRemove);
    const updatedStock = { ...material.colorStock };
    delete updatedStock[colorToRemove];

    onUpdate(material.materialId, { colorOptions: updatedColors, colorStock: updatedStock });
  };

  const handleToggleColor = (color: string, currentInStock: boolean) => {
    onToggleStock(material.materialId, { colorStock: { [color]: !currentInStock } });
  };

  const handleUpdateColorCost = (color: string, spoolCost: number) => {
    const existing = material.colorSpoolInfo?.[color];
    if (!existing) return;

    onUpdate(material.materialId, {
      colorSpoolInfo: { [color]: { ...existing, spoolCost } },
    });
  };

  return (
    <div
      style={{
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border-default)',
        backgroundColor: 'var(--bg-surface)',
        padding: 'var(--space-6)',
      }}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start justify-between" style={{ marginBottom: 'var(--space-4)', gap: 'var(--space-3)' }}>
        <div style={{ flex: 1 }}>
          {isEditing ? (
            <EditFields editForm={editForm} setEditForm={setEditForm} />
          ) : (
            <>
              <div className="flex items-center" style={{ gap: 'var(--space-2)', marginBottom: 'var(--space-1)' }}>
                <h3
                  style={{
                    fontSize: 'var(--text-lg)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--text-primary)',
                  }}
                >
                  {material.name}
                </h3>
                <span
                  style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--text-tertiary)',
                    fontFamily: 'monospace',
                  }}
                >
                  {material.materialId}
                </span>
              </div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)', marginBottom: 'var(--space-2)' }}>
                {material.description}
              </p>
              <div className="flex flex-wrap" style={{ gap: 'var(--space-4)', fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                <span>${material.ratePerCm3}/cm3 rate</span>
                <span>{material.densityGPerCm3} g/cm3</span>
                <span>{material.estimatedDaysBase}d lead time</span>
                <span>${totalInvestment.toFixed(2)} total spool investment ({material.colorOptions.length} spool{material.colorOptions.length !== 1 ? 's' : ''})</span>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center flex-wrap" style={{ gap: 'var(--space-2)', flexShrink: 0 }}>
          {/* Material-level stock toggle */}
          {!isEditing && (
            <button
              type="button"
              onClick={() => onToggleStock(material.materialId, { isActive: !material.isActive })}
              disabled={isUpdating}
              title={material.isActive ? 'Mark material out of stock' : 'Mark material in stock'}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                padding: 'var(--space-1) var(--space-2)',
                borderRadius: 'var(--radius-full)',
                border: `1px solid ${material.isActive ? 'var(--color-success-500)' : 'var(--color-error-500)'}`,
                backgroundColor: material.isActive ? 'var(--bg-success-subtle)' : 'var(--bg-error-subtle)',
                color: material.isActive ? 'var(--color-success-600)' : 'var(--color-error-600)',
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-weight-medium)',
                cursor: isUpdating ? 'not-allowed' : 'pointer',
              }}
            >
              <span
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: material.isActive ? 'var(--color-success-500)' : 'var(--color-error-500)',
                }}
              />
              {material.isActive ? 'In Stock' : 'Out of Stock'}
            </button>
          )}

          {isEditing ? (
            <>
              <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)} disabled={isUpdating}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSaveEdit} disabled={isUpdating}>
                Save
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="ghost" onClick={() => setIsEditing(true)} disabled={isUpdating}>
                Edit
              </Button>
              <Button size="sm" variant="danger" onClick={() => setConfirmDelete(true)} disabled={isUpdating}>
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Colors with per-spool costs */}
      <div style={{ marginBottom: 'var(--space-3)' }}>
        <span
          style={{
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text-secondary)',
            display: 'block',
            marginBottom: 'var(--space-2)',
          }}
        >
          Colors ({material.colorOptions.length}) — each color = separate spool
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {material.colorOptions.map((color) => {
            const inStock = material.colorStock[color] !== false;
            const spoolInfo = material.colorSpoolInfo?.[color];
            return (
              <ColorRow
                key={color}
                color={color}
                inStock={inStock}
                spoolCost={spoolInfo?.spoolCost}
                spoolWeightGrams={spoolInfo?.spoolWeightGrams}
                onToggle={() => handleToggleColor(color, inStock)}
                onRemove={() => handleRemoveColor(color)}
                onUpdateCost={(cost) => handleUpdateColorCost(color, cost)}
                disabled={isUpdating}
              />
            );
          })}
        </div>
      </div>

      {/* Add Color */}
      <div className="flex flex-wrap" style={{ gap: 'var(--space-2)', alignItems: 'flex-end' }}>
        <div style={{ maxWidth: '160px' }}>
          <Input
            id={`add-color-${material.materialId}`}
            label=""
            placeholder="Color name..."
            value={newColor}
            onChange={(e) => setNewColor(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddColor();
              }
            }}
            style={{ fontSize: 'var(--text-sm)', padding: 'var(--space-1) var(--space-2)' }}
          />
        </div>
        <div style={{ maxWidth: '100px' }}>
          <Input
            id={`add-cost-${material.materialId}`}
            label=""
            type="number"
            step="0.01"
            placeholder={`$${getDefaultSpoolCost()}`}
            value={newColorCost}
            onChange={(e) => setNewColorCost(e.target.value)}
            style={{ fontSize: 'var(--text-sm)', padding: 'var(--space-1) var(--space-2)' }}
          />
        </div>
        <div style={{ maxWidth: '100px' }}>
          <Input
            id={`add-weight-${material.materialId}`}
            label=""
            type="number"
            placeholder={`${getDefaultSpoolWeight()}g`}
            value={newColorWeight}
            onChange={(e) => setNewColorWeight(e.target.value)}
            style={{ fontSize: 'var(--text-sm)', padding: 'var(--space-1) var(--space-2)' }}
          />
        </div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={handleAddColor}
          disabled={isUpdating || !newColor.trim()}
        >
          Add
        </Button>
      </div>

      {confirmDelete && (
        <ConfirmModal
          title="Delete material"
          message={`Are you sure you want to delete "${material.name}"? This will remove the material and all its color options. This action cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Keep"
          onConfirm={() => onDelete(material.materialId)}
          onClose={() => setConfirmDelete(false)}
          isPending={isUpdating}
        />
      )}
    </div>
  );
};

// --- Sub-components ---

interface EditFieldsProps {
  readonly editForm: {
    name: string;
    description: string;
    densityGPerCm3: string;
    estimatedDaysBase: string;
  };
  readonly setEditForm: React.Dispatch<React.SetStateAction<EditFieldsProps['editForm']>>;
}

const EditFields = ({ editForm, setEditForm }: EditFieldsProps) => {
  const update = (field: string, value: string) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 'var(--space-3)' }}>
      <Input id="edit-name" label="Name" value={editForm.name} onChange={(e) => update('name', e.target.value)} />
      <Input id="edit-density" label="Density (g/cm3)" type="number" step="0.01" value={editForm.densityGPerCm3} onChange={(e) => update('densityGPerCm3', e.target.value)} />
      <Input id="edit-days" label="Est. Days" type="number" min="1" value={editForm.estimatedDaysBase} onChange={(e) => update('estimatedDaysBase', e.target.value)} />
      <div style={{ gridColumn: '1 / -1' }}>
        <Input id="edit-desc" label="Description" value={editForm.description} onChange={(e) => update('description', e.target.value)} />
      </div>
    </div>
  );
};

interface ColorRowProps {
  readonly color: string;
  readonly inStock: boolean;
  readonly spoolCost?: number;
  readonly spoolWeightGrams?: number;
  readonly onToggle: () => void;
  readonly onRemove: () => void;
  readonly onUpdateCost: (cost: number) => void;
  readonly disabled: boolean;
}

const ColorRow = ({ color, inStock, spoolCost, spoolWeightGrams, onToggle, onRemove, onUpdateCost, disabled }: ColorRowProps) => {
  const [editingCost, setEditingCost] = useState(false);
  const [costValue, setCostValue] = useState(String(spoolCost ?? ''));

  const handleSaveCost = () => {
    const cost = Number(costValue);
    if (!isNaN(cost) && cost > 0) {
      onUpdateCost(cost);
    }
    setEditingCost(false);
  };

  return (
    <div
      className="flex items-center"
      style={{
        gap: 'var(--space-2)',
        padding: 'var(--space-1) var(--space-2)',
        borderRadius: 'var(--radius-md)',
        border: `1px solid ${inStock ? 'var(--color-success-500)' : 'var(--color-error-500)'}`,
        backgroundColor: inStock ? 'var(--bg-success-subtle)' : 'var(--bg-error-subtle)',
        fontSize: 'var(--text-xs)',
      }}
    >
      {/* Stock toggle + name */}
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        title={inStock ? 'Mark out of stock' : 'Mark in stock'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--space-1)',
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: disabled ? 'not-allowed' : 'pointer',
          color: inStock ? 'var(--color-success-600)' : 'var(--color-error-600)',
          fontSize: 'inherit',
          fontWeight: 'var(--font-weight-medium)',
          minWidth: '80px',
        }}
      >
        <span
          style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: inStock ? 'var(--color-success-500)' : 'var(--color-error-500)',
          }}
        />
        {color}
      </button>

      {/* Spool cost */}
      {spoolCost !== undefined && (
        <span style={{ color: 'var(--text-secondary)', marginLeft: 'auto' }}>
          {editingCost ? (
            <span className="flex items-center" style={{ gap: '4px' }}>
              $<input
                type="number"
                step="0.01"
                value={costValue}
                onChange={(e) => setCostValue(e.target.value)}
                onBlur={handleSaveCost}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveCost();
                  if (e.key === 'Escape') setEditingCost(false);
                }}
                autoFocus
                style={{
                  width: '60px',
                  fontSize: 'inherit',
                  border: '1px solid var(--border-default)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '1px 4px',
                  backgroundColor: 'var(--bg-surface)',
                }}
              />
              / {spoolWeightGrams}g
            </span>
          ) : (
            <button
              type="button"
              onClick={() => { setCostValue(String(spoolCost)); setEditingCost(true); }}
              disabled={disabled}
              title="Click to edit spool cost"
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: disabled ? 'not-allowed' : 'pointer',
                color: 'inherit',
                fontSize: 'inherit',
                textDecoration: 'underline dotted',
              }}
            >
              ${spoolCost} / {spoolWeightGrams}g
            </button>
          )}
        </span>
      )}

      {/* Delete */}
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        title={`Delete ${color}`}
        style={{
          background: 'none',
          border: 'none',
          padding: '0 2px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          color: '#9ca3af',
          fontSize: '14px',
          lineHeight: 1,
          marginLeft: spoolCost === undefined ? 'auto' : '0',
        }}
      >
        x
      </button>
    </div>
  );
};
